import { Icon } from '~/modules/elements/akui/icon';
import styles from '~/modules/game-engine/drawing/styles';
import events from '~/modules/game-events/game-events';
import { useEventListener } from '~/modules/game-events/hooks';
import { RemoteCurrentSong } from '~/modules/remote-mic/network/client/subscriptions';
import useMicLevel from '~/routes/remote-mic/hooks/use-mic-level';

interface Props {
  song: RemoteCurrentSong;
  isMicOn: boolean;
  onPause: () => void;
  pauseEnabled: boolean;
}

// The arrow pad is gone here on purpose: while a song runs the game accepts no arrow controls, so a
// pad would be four dead buttons. What the space is worth instead is the one thing this screen is
// for - showing the phone is listening - so the microphone grows a ring that moves with the voice.
//
// No clock and no progress track. The phone is never told the song position, and the screen was
// drawn without one so it stays that way.
//
// The level stage that used to sit under this screen is stood down while it is up (see
// `microphone.tsx`), which makes the ring and the slot line below the only place either is said.
export default function SingingScreen({ song, isMicOn, onPause, pauseEnabled }: Props) {
  const [playerNumber] = useEventListener(events.remoteMicPlayerSet, true) ?? [null];
  const { level } = useMicLevel();

  // A connected phone with write permission gets this screen whether or not it took a slot, and
  // while the song runs it can no longer take one. So the eyebrow states what is true of the room
  // rather than of this phone: telling a phone that is not in the game that it is singing would be
  // wrong at the one moment it can do nothing about it.
  const joined = playerNumber !== null;
  const colour = joined ? styles.colors.players[playerNumber].perfect.fill : styles.colors.lines.normal.fill;

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-[2.125rem]">
        <div className="relative flex aspect-square max-h-full w-full max-w-[16.625rem] items-center justify-center">
          <span aria-hidden className="absolute inset-0 rounded-full shadow-[inset_0_0_0_3px_rgba(255,255,255,0.12)]" />
          <span
            aria-hidden
            data-test="level-ring"
            className="absolute inset-0 rounded-full transition-transform duration-150 ease-out"
            style={{
              boxShadow: `inset 0 0 0 0.5rem ${colour}`,
              // Never all the way down to nothing: a silent ring that has vanished reads as a
              // microphone that has stopped working.
              transform: `scale(${isMicOn ? 0.82 + level * 0.18 : 0.82})`,
              opacity: isMicOn ? 0.5 + level * 0.45 : 0.35,
            }}
          />
          <Icon icon="ic:baseline-mic" size={25} style={{ color: isMicOn ? colour : 'rgba(255,255,255,.35)' }} />
        </div>
        <div className="flex max-w-full flex-col items-center gap-1">
          <span className="text-active text-[0.75rem] font-bold tracking-[0.16em] uppercase">
            {joined ? "You're singing" : 'Now playing'}
          </span>
          <span
            className="max-w-full truncate text-center text-[1.5625rem] leading-[1.2] font-bold"
            data-test="singing-song-title">
            {song.title}
          </span>
          <span className="text-[0.9375rem] text-white/80">{song.artist}</span>
        </div>
      </div>
      <div className="flex shrink-0 flex-col gap-3 pb-1">
        {/* The slot, in the stage's own vocabulary and under its own test id: while this screen is
            up it is the only place on the phone the slot is named, so anything that used to read it
            off the stage reads it here instead. */}
        {joined && (
          <span
            className="flex items-center justify-center gap-1.5 text-[0.8125rem] font-bold tracking-[0.08em] text-white/55 uppercase"
            data-test="player-slot"
            data-player-number={playerNumber}>
            <span aria-hidden className="size-[0.5625rem] shrink-0 rounded-full" style={{ background: colour }} />P
            {playerNumber + 1} · you
          </span>
        )}
        <button
          type="button"
          onClick={onPause}
          disabled={!pauseEnabled}
          data-test="keyboard-backspace"
          className="flex h-15.5 cursor-pointer items-center justify-center gap-2.5 rounded-[1.125rem] border-none bg-white/6 text-[1.0625rem] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)] transition-transform duration-100 active:scale-[0.98] disabled:opacity-40">
          <Icon icon="ic:baseline-pause" size={5.5} />
          Pause menu
        </button>
      </div>
    </>
  );
}
